// Loading and saving goals data, and turning stored answers into engine inputs.

import { useQuery } from '@tanstack/react-query';
import { todayISO } from '../format';
import { supabase, unwrap, unwrapMaybe } from '../supabase';
import {
  backSolve,
  DEFAULT_CALL_TO_MEETING,
  generateMilestones,
  ROLE_TEMPLATES,
  urgencyTier,
  type Experience,
  type Metric,
  type Plan,
  type PlanInput,
  type Role,
  type StreamInput,
  type UrgencyAnswers,
  type UrgencyTier,
} from './engine';

/** Questionnaire answers as stored in goal_profiles.answers (spec 4.1). */
export interface GoalAnswers {
  firstName: string;
  experience: Experience;
  roles: Role[];
  propertyTypes: string[];
  city: string;
  territory: string;
  team: 'solo' | 'team';
  brokerageType: string;
  /** Percent you keep after the house, and after a co-broker/team split (0–100). */
  houseSplitPct: number;
  coBrokerSplitPct: number;
  incomeGoal: number;
  workingWeeks: number;
  prospectingHours: number | null;
  callsPerHour: number;
  pipeline: { activeListings: number | null; activeClients: number | null; databaseSize: number | null };
  channels: string[];
  streams: StreamInput[];
}

export const PROPERTY_TYPES = ['Industrial', 'Retail', 'Multifamily', 'Office', 'Medical office', 'Self storage', 'Land', 'Generalist'];
export const CHANNELS = ['Cold calls', 'Door knocking', 'Email', 'Mailers', 'Newsletter', 'Referrals'];
export const BROKERAGE_TYPES = ['National', 'Regional', 'Boutique', 'Independent', 'Own firm'];
export const EXPERIENCE_LABEL: Record<Experience, string> = { lt1: 'Under 1 year', '1to3': '1–3 years', '3to10': '3–10 years', '10plus': '10+ years' };

/** Starting stream for a role (placeholders; the user edits them). */
export function defaultStream(role: Role, experience: Experience, share = 1): StreamInput {
  const sale = role === 'investment_sales' || role === 'acquisition' || role === 'buyside';
  return {
    role,
    share,
    dealSize: sale ? 1_000_000 : 0,
    feeMode: sale ? 'pct' : 'amount',
    feePct: role === 'investment_sales' ? 0.06 : 0.02,
    feeAmount: sale ? 0 : 15_000,
    callToMeeting: DEFAULT_CALL_TO_MEETING[experience],
    rates: ROLE_TEMPLATES[role].steps.map((s) => s.rate),
  };
}

export function emptyAnswers(firstName = ''): GoalAnswers {
  return {
    firstName,
    experience: '1to3',
    roles: [],
    propertyTypes: [],
    city: '',
    territory: '',
    team: 'solo',
    brokerageType: '',
    houseSplitPct: 60,
    coBrokerSplitPct: 100,
    incomeGoal: 250_000,
    workingWeeks: 48,
    prospectingHours: null,
    callsPerHour: 15,
    pipeline: { activeListings: null, activeClients: null, databaseSize: null },
    channels: ['Cold calls'],
    streams: [],
  };
}

export const emptyUrgency = (): UrgencyAnswers => ({ support: 'some', savings: null, passiveIncome: null, secondJob: null, familyCovers: null, connections: null });

export function planInput(a: GoalAnswers, u: UrgencyAnswers): PlanInput {
  return {
    incomeGoal: a.incomeGoal,
    houseSplit: a.houseSplitPct / 100,
    coBrokerSplit: a.coBrokerSplitPct / 100,
    workingWeeks: a.workingWeeks,
    // A second job means less time to prospect (spec 4.2): halve stated hours for the realism check.
    prospectingHoursPerWeek: a.prospectingHours == null ? null : u.secondJob ? a.prospectingHours / 2 : a.prospectingHours,
    callsPerHour: a.callsPerHour,
    secondJob: !!u.secondJob,
    streams: a.streams,
  };
}

export type GoalProfileRow = {
  id: string;
  version: number;
  effective_from: string;
  answers: GoalAnswers;
  urgency_answers: UrgencyAnswers;
  urgency_tier: UrgencyTier | null;
  urgency_override: UrgencyTier | null;
  horizon_months: number;
};

export function useGoals() {
  return useQuery({
    queryKey: ['goals'],
    queryFn: async () => {
      const [settings, profile, coach, first] = await Promise.all([
        supabase.from('app_settings').select('first_name, digest_timezone, fiscal_year_start_month').maybeSingle(),
        supabase.from('goal_profiles').select('*').is('effective_to', null).order('version', { ascending: false }).limit(1).maybeSingle(),
        supabase.from('coach_settings').select('*').maybeSingle(),
        supabase.from('goal_profiles').select('effective_from').order('version').limit(1).maybeSingle(),
      ]);
      const p = unwrapMaybe(profile) as unknown as GoalProfileRow | null;
      const milestones = p ? unwrap(await supabase.from('goal_milestones').select('*').eq('profile_id', p.id).order('due_date')) : [];
      const s = unwrapMaybe(settings);
      const plan: Plan | null = p ? backSolve(planInput(p.answers, p.urgency_answers)) : null;
      return {
        firstName: s?.first_name ?? null,
        timezone: s?.digest_timezone ?? 'America/New_York',
        fiscalStartMonth: s?.fiscal_year_start_month ?? 1,
        profile: p,
        /** When goals started (first version): targets pro-rate and the revenue ramp count from here. */
        goalStart: unwrapMaybe(first)?.effective_from ?? p?.effective_from ?? todayISO(),
        plan,
        milestones,
        coach: unwrapMaybe(coach),
      };
    },
  });
}

export type Actuals = { calls: number; meetings: number; stages: Record<string, number>; revenue: number; weighted_outlook: number };

export function useActuals(from: string, to: string, timezone: string, enabled = true) {
  return useQuery({
    queryKey: ['goal-actuals', from, to, timezone],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('goal_actuals', { p_from: from, p_to: to, p_tz: timezone });
      if (error) throw new Error(error.message);
      const a = data as unknown as Actuals;
      return { ...a, revenue: Number(a.revenue), weighted_outlook: Number(a.weighted_outlook) };
    },
  });
}

/** The actual count for a metric: stage metrics count entries into the stages of the user's roles. */
export function actualFor(metric: Metric, a: Actuals, roles: Role[]): number {
  if (metric === 'calls') return a.calls;
  if (metric === 'meetings') return a.meetings;
  if (metric === 'revenue') return a.revenue;
  let n = 0;
  for (const r of roles)
    for (const step of ROLE_TEMPLATES[r].steps)
      if (step.metric === metric) for (const st of step.stages) n += a.stages[`${st.dealType}:${st.stage}`] ?? 0;
  return n;
}

/**
 * Save the plan as a new version (spec 3/11: history kept): closes the current version, writes the
 * new one, regenerates automatic milestones and carries over custom ones. Also saves the first name.
 */
export async function saveProfile(opts: { answers: GoalAnswers; urgency: UrgencyAnswers; override: UrgencyTier | null; horizon: number; current: GoalProfileRow | null; userId: string }) {
  const today = todayISO();
  const { answers, urgency, override, horizon, current } = opts;
  const yesterday = new Date(Date.parse(`${today}T12:00:00Z`) - 86_400_000).toISOString().slice(0, 10);
  if (current) unwrap(await supabase.from('goal_profiles').update({ effective_to: current.effective_from > yesterday ? current.effective_from : yesterday }).eq('id', current.id));
  const row = unwrap(
    await supabase
      .from('goal_profiles')
      .insert({
        version: (current?.version ?? 0) + 1,
        effective_from: today,
        answers: answers as never,
        urgency_answers: urgency as never,
        urgency_tier: urgencyTier(urgency),
        urgency_override: override,
        horizon_months: horizon,
      })
      .select('id')
      .single(),
  );
  const plan = backSolve(planInput(answers, urgency));
  // Milestones count from the first plan's start, so re-editing doesn't restart the clock.
  const start = (current ? (await firstStart()) : null) ?? today;
  const drafts = generateMilestones(plan, start, horizon).filter((m) => m.dueDate >= today);
  const custom = current ? unwrap(await supabase.from('goal_milestones').select('due_date, metric, target, label').eq('profile_id', current.id).eq('auto', false)) : [];
  const rows = [
    ...drafts.map((m) => ({ profile_id: row.id, due_date: m.dueDate, metric: m.metric, target: m.target, label: m.label, auto: true })),
    ...custom.map((m) => ({ profile_id: row.id, due_date: m.due_date, metric: m.metric, target: m.target, label: m.label, auto: false })),
  ];
  if (rows.length) unwrap(await supabase.from('goal_milestones').insert(rows));
  if (answers.firstName.trim())
    unwrap(await supabase.from('app_settings').upsert({ owner_id: opts.userId, first_name: answers.firstName.trim() }, { onConflict: 'owner_id' }));
  return row.id;
}

async function firstStart(): Promise<string | null> {
  const first = unwrapMaybe(await supabase.from('goal_profiles').select('effective_from').order('version').limit(1).maybeSingle());
  return first?.effective_from ?? null;
}
